import {
  setProgress,
  startProgressIndicator,
  stopProgressIndicator,
} from '../progress';

import { makeJsonRequest } from '../ajax';

import {
  startLoadingIndicator,
  stopLoadingIndicator,
} from '../loading';

import { isMobile } from '../device';

import { localStorageGet, localStorageSet } from '../local-storage';

import { rememberScrollPositionOnce } from '../remember-scroll-position';

// lifted from https://stackoverflow.com/a/70789108
function getPromiseFromEvent(item, event) {
  return new Promise((resolve) => {
    const listener = () => {
      item.removeEventListener(event, listener);
      resolve();
    }
    item.addEventListener(event, listener);
  })
}

export default function album({
  appName,
  bulkDeleteUrl,
  chunkSizeBytes,
  csrfToken,
  favouriteIds,
  maxUploadBytes,
  uploadUrl,
}) {
  return {
    isMobile,

    setProgress,
    startProgressIndicator,
    stopProgressIndicator,

    startLoadingIndicator,
    stopLoadingIndicator,

    selectedArticleIds: {},

    deletedArticleIds: {},

    favourites: {},

    showNotes: false,

    init() {
      this.refreshSelectedArticleIds();
      for (const id of favouriteIds) this.favourites[id] = true;
    },

    toggleNotes() {
      this.showNotes = !this.showNotes;
    },

    toggleBulkSelectArticles() {
      const { target } = event;
      const { checked } = target;
      const inputs = document.getElementsByClassName('js-article-id-input');
      for (let i = 0; i !== inputs.length; i++) {
        const input = inputs[i];
        const { articleId } = input.dataset;
        if (checked) {
          input.checked = true;
          this.selectedArticleIds[articleId] = true;
        } else {
          input.checked = false;
          delete this.selectedArticleIds[articleId];
        }
      }
      const bulkSelectors = document.getElementsByClassName('js-article-bulk-input');
      for (let i = 0; i !== bulkSelectors.length; i++) bulkSelectors[i].checked = checked;
    },

    toggleArticleSelected() {
      this.refreshSelectedArticleIds();
    },

    refreshSelectedArticleIds() {
      let numSelected = 0;
      let numUnselected = 0;
      const inputs = document.getElementsByClassName('js-article-id-input');
      for (let i = 0; i !== inputs.length; i++) {
        const input = inputs[i];
        const { articleId } = input.dataset;
        if (input.checked) {
          this.selectedArticleIds[articleId] = true;
          numSelected++;
        } else {
          delete this.selectedArticleIds[articleId];
          numUnselected++;
        }
      }
      const bulkSelectors = document.getElementsByClassName('js-article-bulk-input');
      for (let i = 0; i !== bulkSelectors.length; i++) {
        const bulkSelector = bulkSelectors[i];
        if (numSelected !== 0 && numUnselected !== 0) {
          bulkSelector.indeterminate = true;
        } else if (numSelected === 0) {
          bulkSelector.indeterminate = false;
          bulkSelector.checked = false;
        } else {
          bulkSelector.indeterminate = false;
          bulkSelector.checked = true;
        }
      }
    },

    selectedArticleIdsValue() {
      const selectedArticleIds = [];
      const keys = Object.keys(this.selectedArticleIds);
      for (let i = 0; i !== keys.length; i++) {
        if (this.selectedArticleIds[keys[i]]) {
          selectedArticleIds.push(parseInt(keys[i]));
        }
      }
      return selectedArticleIds.join(',');
    },

    confirmDeleteArticle() {
      const e = event;
      window.confirmationModal.ask(
        'Delete this item?',
        () => {
          rememberScrollPositionOnce();
          this.startLoadingIndicator();
          e.target.submit();
        },
      );
    },

    async toggleFavourite(articleId, makeFavourite) {
      const url = this.$refs[`toggleArticle-${articleId}`].dataset.toggleFavouriteUrl;
      return makeJsonRequest({
        url,
        method: (makeFavourite ? 'POST' : 'DELETE'),
        bodyData: { csrfToken },
        onSuccess: (responseBodyData) => this.favourites[articleId] = makeFavourite,
      });
    },

    async addToFavourites(articleId) {
      return this.toggleFavourite(articleId, true);
    },

    removeFromFavourites(articleId) {
      return this.toggleFavourite(articleId, false);
    },

    articleIsFavourite(articleId) {
      return this.favourites[articleId];
    },

    articleIsDeleted(articleId) {
      return this.deletedArticleIds[articleId];
    },

    setAndReturnSelectedArticleIds() {
      const selectedArticleIds = this.selectedArticleIdsValue();
      const inputs = document.getElementsByClassName('js-selected-article-ids');
      for (let i = 0; i !== inputs.length; i++) {
        inputs[i].value = selectedArticleIds;
      }
      return selectedArticleIds;
    },

    archiveAlbum(formName) {
      window.confirmationModal.ask(
        'Archive album?',
        async () => {
          this.startLoadingIndicator();
          document[formName].submit();
        },
      );
    },

    unarchiveAlbum(formName) {
      window.confirmationModal.ask(
        'Unarchive album?',
        async () => {
          this.startLoadingIndicator();
          document[formName].submit();
        },
      );
    },

    deleteSelectedArticles(formName) {
      const selectedArticleIds = this.setAndReturnSelectedArticleIds();
      const numArticles = selectedArticleIds.split(',').length;
      const question = (numArticles == 1 ? 'Delete this item?' : `Delete these ${numArticles} items?`);

      window.confirmationModal.ask(
        question,
        async () => {
          rememberScrollPositionOnce();
          this.startLoadingIndicator();
          document[formName].submit();
        },
      );
    },

    bulkEditDateTakenSelectedArticles() {
      this.setAndReturnSelectedArticleIds();
      event.target.submit();
    },

    bulkCopySelectedArticles() {
      this.setAndReturnSelectedArticleIds();
      event.target.submit();
    },

    get selecting() {
      return Object.keys(this.selectedArticleIds).length !== 0;
    },

    async handleUploadError(message) {
      this.stopProgressIndicator();
      await window.confirmationModal.tellBlocking(message);
    },

    countChunks(fileSize) {
      return Math.ceil(fileSize / chunkSizeBytes);
    },

    async registerSelectedFiles(formId) {
      const files = event.target.files;
      const numFiles = files.length;
      const totalChunks = [...files].reduce((numChunks, file) => numChunks + this.countChunks(file.size), 0);
      const fileWord = (numFiles === 1 ? 'file' : 'files');
      const progressTemplate = `Uploading ${numFiles} ${fileWord}\n\n{percentage} complete`;

      const startP = () => this.startProgressIndicator();
      const setP = (chunksUploaded) => this.setProgress(
        chunksUploaded,
        totalChunks,
        progressTemplate,
        progressTemplate,
      );
      const stopP = () => this.stopProgressIndicator();

      // https://stackoverflow.com/a/44476626
      const timer = ms => new Promise(res => setTimeout(res, ms));

      let chunksUploaded = 0;
      setP(chunksUploaded);
      startP();
      const fieldName = event.target.name;
      for (let i = 0; i !== numFiles; i++) {
        const file = files[i];
        if (file.size > maxUploadBytes) {
          await this.handleUploadError(`${file.name} exceeds maximum upload size`);
          return;
        }
        try {
          chunksUploaded += await this.uploadFile(fieldName, file, chunksUploaded, setP);
        } catch (err) {
          await this.handleUploadError(
            err.message ||
            'Something went wrong.\nPlease reload the page and try again.',
          );
          break;
        }
      }
      window.location.reload();
    },

    async uploadFile(fieldName, file, chunksAlreadyUploaded, setProgress) {
      const totalChunks = this.countChunks(file.size);

      const readNextChunk = async (articleNid, chunkNumber) => {
        return new Promise((resolve, reject) => {
          const start = chunkNumber * chunkSizeBytes;
          const end = Math.min((chunkNumber + 1) * chunkSizeBytes, file.size);
          const currentBlob = file.slice(start, end);
          if (currentBlob.size !== 0) {
            try {
              articleNid = this.uploadChunk(
                articleNid,
                fieldName,
                currentBlob,
                file.name,
                chunkNumber,
                totalChunks,
              );
              chunkNumber++;
              setProgress(chunksAlreadyUploaded + chunkNumber);
              resolve(articleNid);
            } catch (err) {
              reject(err);
            }
          } else {
            resolve();
          }
        });
      }

      let articleNid;
      let chunkNumber = 0;
      for ( ; chunkNumber !== totalChunks; chunkNumber++) {
        articleNid = await readNextChunk(articleNid, chunkNumber);
      }
      return chunkNumber;
    },

    async uploadChunk(articleNid, fieldName, blob, fileName, chunkNumber, totalChunks) {
      const formData = new FormData;
      formData.append(fieldName, blob, fileName);
      formData.append('chunkNumber', chunkNumber);
      formData.append('csrfToken', csrfToken);
      formData.append('totalChunks', totalChunks);
      if (articleNid) {
        formData.append('articleNid', articleNid);
      }

      const controller = new AbortController();
      const { signal } = controller;
      const timeoutId = setTimeout(async () => {
        controller.abort();
        await this.handleUploadError('Server not responding.\nInternet may be unavailable.');
        clearTimeout(timeoutId);
        window.location.reload();
      }, 240000);

      try {
        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers: { 'Accept': 'application/json' },
        });
        const responseStatus = response.status;
        const responseJson = await response.json();
        switch (responseStatus) {
        case 201:
          if (responseJson.fileDone) {
            ; // do nothing
          } else if (responseJson.articleNid) {
            if (articleNid && (responseJson.articleNid !== articleNid)) {
              throw new Error('articleNid has unexpectedly changed');
            } else if (!articleNid) {
              articleNid = responseJson.articleNid;
            }
          } else {
            throw new Error('articleNid unexpectedly absent');
          }
          break;
        default:
          const { message } = responseJson;
          if (message) throw new Error(`Could not upload ${fileName}: ${message}`);
          else throw new Error(`Something went wrong: ${fileName} could not be uploaded`);
        }
      } finally {
        clearTimeout(timeoutId);
      }
      return articleNid;
    },
  };
}
